import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Loader2, Check, Crown, Sparkles, TrendingUp } from 'lucide-react'
import type { MembershipTierConfig, MembershipTier } from '../../types'
import { MembershipBenefitsList } from './MembershipBenefitsList'

interface MembershipTierCardProps {
  tier: MembershipTierConfig
  isCurrentTier?: boolean
  canUpgrade?: boolean
  upgrading?: boolean
  onUpgrade?: (tier: MembershipTier) => void
  variant?: 'full' | 'compact'
}

export function MembershipTierCard({
  tier,
  isCurrentTier = false,
  canUpgrade = true,
  upgrading = false,
  onUpgrade,
  variant = 'full'
}: MembershipTierCardProps) {
  const { t } = useTranslation()

  const getTierIcon = (tierId: string, size: 'sm' | 'md' = 'md') => {
    const sizeClass = size === 'sm' ? 'h-5 w-5' : 'h-6 w-6'
    switch (tierId) {
      case 'book_giant':
        return <Crown className={`${sizeClass} text-yellow-500`} />
      case 'plus':
        return <Sparkles className={`${sizeClass} text-purple-500`} />
      default:
        return <TrendingUp className={`${sizeClass} text-blue-500`} />
    }
  }

  const getTierColor = (tierId: string) => {
    switch (tierId) {
      case 'book_giant':
        return 'border-yellow-500'
      case 'plus':
        return 'border-purple-500'
      default:
        return 'border-blue-500'
    }
  }

  const getTierBgColor = (tierId: string) => {
    switch (tierId) {
      case 'book_giant':
        return 'border-yellow-500 bg-yellow-500/5'
      case 'plus':
        return 'border-purple-500 bg-purple-500/5'
      default:
        return 'border-blue-500 bg-blue-500/5'
    }
  }

  const handleClick = () => {
    if (canUpgrade && !upgrading && onUpgrade) {
      onUpgrade(tier.tier_id as MembershipTier)
    }
  }

  // Compact variant - simpler card for limited space (like in download limit dialog)
  if (variant === 'compact') {
    return (
      <Card
        className={`tier-card-compact cursor-pointer transition-all hover:shadow-md border-2 ${getTierBgColor(tier.tier_id)}`}
        onClick={handleClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {getTierIcon(tier.tier_id, 'sm')}
              <div>
                <p className="font-semibold">{tier.name}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg">${tier.price_monthly_usd}</p>
              <p className="text-xs text-muted-foreground">/{t('common.month', 'month')}</p>
            </div>
          </div>

          {/* Benefits list */}
          <div className="mb-3">
            <MembershipBenefitsList tier={tier} variant="compact" />
          </div>

          <Button
            className="upgrade-membership-btn w-full"
            disabled={upgrading || !canUpgrade}
            onClick={(e) => {
              e.stopPropagation()
              handleClick()
            }}
          >
            {upgrading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('membership.upgrading', 'Upgrading...')}
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                {t('membership.upgradeTo', { tier: tier.name })}
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Full variant - detailed card with header and description
  return (
    <Card
      className={`tier-card-full relative ${getTierColor(tier.tier_id)} ${
        isCurrentTier ? 'border-2' : ''
      }`}
    >
      {isCurrentTier && (
        <Badge className="absolute top-2 right-2" variant="secondary">
          {t('membership.currentPlan', 'Current Plan')}
        </Badge>
      )}
      
      <CardHeader>
        <div className="flex items-center gap-2 mb-2">
          {getTierIcon(tier.tier_id)}
          <CardTitle>{tier.name}</CardTitle>
        </div>
        <CardDescription>
          <span className="text-3xl font-bold text-foreground">
            ${tier.price_monthly_usd}
          </span>
          {tier.price_monthly_usd > 0 && <span className="text-muted-foreground">/{t('common.month', 'month')}</span>}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{tier.description}</p>
        
        <MembershipBenefitsList tier={tier} variant="default" />

        {canUpgrade && onUpgrade && (
          <Button
            className="upgrade-membership-btn w-full mt-4"
            onClick={() => onUpgrade(tier.tier_id as MembershipTier)}
            disabled={upgrading}
          >
            {upgrading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('membership.upgrading', 'Upgrading...')}
              </>
            ) : (
              t('membership.upgradeTo', { tier: tier.name })
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

